/**
 * StudioAcceptInvite.jsx — Public landing page hit when a recipient clicks
 * the email-invite link.  Route: /studio/accept-invite/:token
 *
 * Flow:
 *   1. Mount → GET /studio/invite/:token/preview
 *      • pending → render the "Welcome" card + password form
 *      • accepted | revoked | expired → render the matching error state
 *   2. Submit → POST /studio/invite/:token/accept { password, full_name }
 *      • Backend creates the user, marks the invite accepted, returns auth token
 *      • We persist token + user and navigate to /studio/admin
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { formatDate } from '@/utils/datetime';
import { toast } from 'sonner';
import {
  ArrowRight, Lock, Spinner, Megaphone, ShieldCheck, UserCircle, Crown,
  Warning, Check,
} from '@phosphor-icons/react';
import { StudioThemeProvider, StudioThemeToggle } from '../components/StudioTheme';

const ACCENT = '#6366f1';

const RoleIcon = ({ role }) => {
  if (role === 'owner') return <Crown size={11} weight="fill" />;
  if (role === 'admin') return <ShieldCheck size={11} weight="fill" />;
  return <UserCircle size={11} weight="fill" />;
};

const StudioAcceptInvite = ({ onLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/studio/invite/${token}/preview`);
        setPreview(r.data);
        setFullName(r.data.full_name || '');
      } catch (e) {
        setPreview({ status: 'not_found' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPw) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/studio/invite/${token}/accept`, { password, full_name: fullName });
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      onLogin?.(r.data.user, r.data.token);
      toast.success(`Welcome to Studios, ${(r.data.user.full_name || r.data.user.email).split(' ')[0]}!`);
      navigate('/studio/admin', { replace: true });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to accept invite');
    } finally { setBusy(false); }
  };

  return (
    <StudioThemeProvider className="min-h-screen bg-[var(--s-bg)] text-[var(--s-text)] flex items-center justify-center p-4">
      <div className="absolute top-4 right-4 z-10"><StudioThemeToggle /></div>
      <div data-testid="studio-accept-page" className="w-full flex items-center justify-center">
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(var(--s-grid) 1px, transparent 1px), linear-gradient(90deg, var(--s-grid) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
            <Megaphone size={20} weight="fill" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>Founderboard</div>
            <div className="text-base font-heading font-bold leading-none" style={{ letterSpacing: '-0.01em' }}>Studios — invitation</div>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[var(--s-text-muted)] flex items-center gap-2"><Spinner className="animate-spin" size={14} /> Loading invite…</div>
        ) : !preview || preview.status === 'not_found' ? (
          <NonPendingState data-testid="studio-accept-notfound" icon="warn" title="Invite not found" body="This invite link is invalid or has been deleted. Ask the person who sent it to share a fresh link." />
        ) : preview.status === 'accepted' ? (
          <NonPendingState
            data-testid="studio-accept-already"
            icon="check"
            title="Invite already accepted"
            body={`This invite was already accepted. If that wasn't you, contact the Studios owners. You can sign in directly below.`}
            cta={{ label: 'Go to Studios login', onClick: () => navigate('/studio/login') }}
          />
        ) : preview.status === 'revoked' ? (
          <NonPendingState data-testid="studio-accept-revoked" icon="warn" title="Invite revoked" body="An owner revoked this invite. Reach out to them for a new one." />
        ) : preview.status === 'expired' || preview.expired ? (
          <NonPendingState data-testid="studio-accept-expired" icon="warn" title="Invite expired" body="Invites expire after 7 days. Ask the sender to issue a fresh one." />
        ) : (
          <div data-testid="studio-accept-form-wrap">
            <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
              You're invited to <span style={{ color: ACCENT }}>Studios</span>
            </h1>
            <p className="text-sm text-[var(--s-text-muted)] mb-6 leading-relaxed">
              <strong className="text-[var(--s-text)]">{preview.invited_by_name}</strong> added you as a <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold" style={{ backgroundColor: `${ACCENT}26`, color: ACCENT }}><RoleIcon role={preview.studio_role} /> {preview.studio_role}</span>
              {preview.assigned_count > 0 && <> · catering to <strong className="text-[var(--s-text)]">{preview.assigned_count}</strong> customer{preview.assigned_count === 1 ? '' : 's'}</>}
              {'.'}
            </p>
            <div className="text-[11px] text-[var(--s-text-dim)] mb-6">
              Invited <span className="text-[var(--s-text-muted)]">{preview.email}</span>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="studio-accept-form">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)] mb-2">Your full name</label>
                <input
                  type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                  placeholder="Full name"
                  data-testid="studio-accept-name"
                  className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)] mb-2">Choose a password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  required
                  data-testid="studio-accept-password"
                  className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-[var(--s-text-muted)] mb-2">Confirm password</label>
                <input
                  type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Repeat the password"
                  autoComplete="new-password"
                  required
                  data-testid="studio-accept-password-confirm"
                  className="w-full bg-[var(--s-surface-2)] border border-[var(--s-border)] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--s-border-strong)] placeholder:text-[var(--s-text-faint)]"
                />
              </div>
              <button
                type="submit" disabled={busy}
                data-testid="studio-accept-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />}
                Accept invite & enter Studios
                {!busy && <ArrowRight size={14} weight="bold" />}
              </button>
            </form>
            <div className="mt-6 text-[10px] text-[var(--s-text-dim)] leading-relaxed">
              By accepting, you agree to handle Founderboard customer data responsibly.
              This invite expires {preview.expires_at ? formatDate(preview.expires_at) : 'in 7 days'}.
            </div>
          </div>
        )}
      </div>
      </div>
    </StudioThemeProvider>
  );
};

const NonPendingState = ({ icon, title, body, cta, ...rest }) => (
  <div {...rest}>
    <div className="w-12 h-12 rounded-full border border-[var(--s-border)] flex items-center justify-center mb-4" style={icon === 'warn' ? { backgroundColor: '#ef444420' } : { backgroundColor: `${ACCENT}26` }}>
      {icon === 'warn' ? <Warning size={20} weight="bold" className="text-red-400" /> : <Check size={20} weight="bold" style={{ color: ACCENT }} />}
    </div>
    <h1 className="text-3xl font-heading font-bold leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>{title}</h1>
    <p className="text-sm text-[var(--s-text-muted)] mb-6 leading-relaxed">{body}</p>
    {cta && (
      <button onClick={cta.onClick} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
        {cta.label} <ArrowRight size={13} weight="bold" />
      </button>
    )}
  </div>
);

export default StudioAcceptInvite;
